import type { TierId } from "../services/api/schemas";

/**
 * Bundle tiers — the DECIDED ladder.
 *
 * Source of truth for the economics: kev-docs/kev-token-economics.html (every
 * assumption is a named constant there — change the constant, not a copy here).
 *
 * REPRICED 2026-09-03 (Ned) to $4.99 per model · $9.99 Premium · $19.99 Pro,
 * monthly, top-up 100cr/$7.99 unchanged. The 2026-08-31 ladder it replaces was
 * $19.99 / $49.99 / $89.99 monthly (which had replaced the weekly one). Credit allowances and daily
 * game caps were NOT changed, which matters more than it used to — see the
 * warning below. It supersedes both the 2026-08-03 weekly ladder ($14.99/$29.99/
 * $49.99 per week) and sports-ai-platform-plan.md §2.2's Free/Punter/Pro/Elite
 * names.
 *
 * ⚠️ THIS LADDER IS NEARLY THE ORIGINAL ONE, AND THAT IS A TRAP. The pre-weekly
 * ladder was monthly $19.99/$49.99/$99.99. Two of the three now match exactly
 * and Pro is $10 below it. api/SECURITY-CHECKLIST.md's go-live step validates
 * Stripe prices against those old numbers, so a launch check against it would
 * pass on per-model and Premium and fail on Pro alone — which reads as a typo in
 * the Pro price rather than as a stale document. That checklist is updated in
 * the same change as this file; sports-ai-platform-plan.md and
 * build-task-list.md still carry the old ladder, along with the superseded
 * $5.99/100cr top-up.
 *
 * Prices are stored per MONTH and charged per month — one number. ./pricing no
 * longer converts anything, and the note there explains why removing the
 * conversion was the point rather than a side effect.
 *
 * ⚠️ CREDIT ALLOWANCES ARE NOW A COST CONTROL. At the 2026-08-31 prices a
 * subscriber who spends every credit costs real margin: Pro clears single digits
 * at full use, and under the 30% founding discount Premium and Pro are both sold
 * below cost on purpose (Ned, 2026-08-31). The ceiling is what makes that a
 * fixed monthly number instead of an open tab, and it is the ONLY per-user limit
 * left — api/job_cost_breaker.py has been blind since chat moved off Bedrock.
 * Raising PREMIUM_CREDITS or PRO_CREDITS is a pricing decision, not a perk.
 *
 * Two semantics worth not re-deriving (memory: kev-token-economics):
 *  - CREDITS are charged PER TURN (follow-ups are the biggest cost driver);
 *    "10 credits ≈ a game of tips" is a marketing unit, not a billing one.
 *  - The DAILY BUDGET is in TOKENS (140k/420k/940k), charged on every turn
 *    including follow-ups — it's the scarcity lever; the monthly pool only
 *    bounds whale risk. It counted distinct GAMES (3/9/20) until 2026-08-28,
 *    which is why customer copy now says "about N games" and never "up to".
 */

export interface PlanDef {
  id: Exclude<TierId, "free">;
  name: string;
  priceId: string;
  /** Per month, and the same figure the Stripe price charges. */
  monthlyPriceAud: number;
  monthlyCredits: number;
  blurb: string;
  /** Rendered verbatim. Everything about model COUNTS is derived, never typed. */
  perks: string[];
}

/**
 * The daily budget, said honestly: the number of games is an estimate now.
 *
 * ── "ABOUT", AND WHY IT IS NOT HEDGING ───────────────────────────────────────
 * This said "Up to 9 games a day" until 2026-08-28, when the server stopped
 * counting games at all. `kev_security.DAILY_TOKEN_BUDGET` is a TOKEN budget
 * (140k/420k/940k) charged on every turn, follow-ups included, so how many games
 * it buys depends on how much the customer talks: about 9 for a typical session
 * on Premium, more like 15 for someone terse and 6 for someone chatty.
 *
 * "Up to 9" is therefore a number we can no longer deliver on demand — and it
 * was already the weaker claim for a second reason that still stands (Ned,
 * 2026-08-08): every turn also spends credits, a season-futures read spends a
 * lot of them, and someone leaning on futures can exhaust 1,500 credits well
 * before nine games in a day.
 *
 * Both are the same exposure the file header flags twice over. A headline figure
 * reality doesn't deliver is ACL misleading conduct, not a copy nit. So the line
 * says "about", and the plan card prints the credit allowance directly above it
 * ("1,500 credits a month"), so the reader can see which one they will hit.
 * The line itself stopped repeating the credit figure on 2026-10-05 (Ned's
 * site cut list).
 *
 * ⚠️ THE SERVER IS THE SOURCE OF THIS NUMBER. If DAILY_TOKEN_BUDGET moves, the
 * game figures here are wrong and nothing will fail to tell you — they are
 * marketing arithmetic over a constant in another repo. That is the same trap as
 * the price ladder, and the same answer applies: change them together.
 */
const gamesPerk = (games: number) => `About ${games} games a day`;

const PREMIUM_CREDITS = 1500;
const PRO_CREDITS = 3300;

/**
 * ⚠️ THE priceIds BELOW ARE STRIPE LIVE-MODE IDS, AND NOTHING IN THE STRING SAYS SO.
 *
 * They were TEST-MODE ids until 2026-08-27, and this block warned that no test on
 * either side could notice one reaching a live account — "the first signal would
 * be a customer's checkout failing on the most expensive thing we sell."
 *
 * That is exactly what happened, in the mirror image. These ids went live here
 * while RIPK's `handler_billing.TIER_PRICE_IDS` still held the test ones, so the
 * browser sent `price_1U8vs…`, the server expected `price_1U1x…`, and every
 * Premium and Pro checkout was refused `price_grant_mismatch` in BOTH
 * environments — the server's `KEV_TIER_PRICE_IDS` override had no CDK wiring, so
 * there was no way to correct it without a deploy that would wipe it again.
 *
 * The safety net this block said did not exist now does:
 * `api/test_billing_checkout.py::TierPriceIdsMatchWhatTheBrowserSends` reads this
 * file and compares both directions against the server's map, plus the Stripe
 * account segment. It skips without `KEV_WEB_DIR`, so it is a check that has to
 * be RUN with both checkouts present, not one that runs itself in CI here.
 *
 * What has not changed:
 *
 *   - These exist only here and are compiled into the bundle. The tiers are the
 *     two most expensive things anyone can buy.
 *   - `/billing/checkout-session` validates the SHAPE (`price_…`) and then that
 *     the price buys what the grant claims. A wrong-mode id passes the shape
 *     check exactly as well as a right one; only the drift test above and
 *     `npm run verify:prices -- --live` can tell them apart.
 *
 * A re-price is NEW ids from the live-mode account, never an edit: a Stripe price
 * is immutable once used, and test and live ids never interchange.
 * `kev-docs/kev-stripe-setup.md` is the procedure.
 *
 * `plans.test.ts` asserts this warning is still here, because the whole risk is
 * that the file looks unremarkable.
 *
 * ═══ AND DEV SUBSTITUTES ITS OWN, AT BUILD TIME ═══
 *
 * `VITE_TIER_PRICE_IDS` re-points the ids below for one build. It is the client-side
 * twin of `KEV_TIER_PRICE_IDS` on the server and `KEV_MODEL_PRICE_IDS` in
 * `api/kev_prices.py`, with the SAME polarity and for the same reason: the file holds
 * the ids a paying customer is charged against, because that is where being wrong
 * costs money rather than an afternoon in dev.
 *
 * ⚠️ WHY THIS HAD TO EXIST. Model prices reach the browser from `GET /catalogue`, so
 * they are already per-environment, and `Pricing.tsx` deliberately buys only from that
 * server copy — "paint from the seed, buy only from the server". TIER prices had no
 * such route: nothing serves them, so the LIVE ids below were compiled into DEV's
 * bundle as well, while dev's Lambda expects test-mode ids. Every Premium and Pro
 * checkout on dev was refused `price_grant_mismatch` before Stripe was ever called —
 * presenting as a button that does nothing, because no session exists to redirect to.
 * Found 2026-08-29. This closes the half of that comment which had no server to buy
 * from; serving tiers from `/catalogue` would close it the other way, and is the
 * better fix on the day that contract is next opened.
 *
 * A malformed value falls back to the ids below rather than to nothing, exactly as
 * `apply_model_price_overrides` does: "no price on file" is what makes a plan
 * unsellable, and a typo in a deploy variable is not a decision to stop selling.
 *
 * IT SUBSTITUTES ONLY. An entry naming a tier that does not exist here is ignored, so
 * the override can re-point a plan and can never invent one.
 */

/**
 * `{tier: priceId}` from `VITE_TIER_PRICE_IDS` (JSON). Empty when unset or unusable.
 *
 * `import.meta.env?.` — the optional chain is load-bearing outside the browser, for
 * the reason given above `PURCHASING_LIVE`: `scripts/` imports this file and runs it
 * on plain Node, which defines no `import.meta.env`. Without it `verify-prices` dies
 * before it can check anything.
 */
function tierPriceOverrides(): Readonly<Record<string, string>> {
  const raw = import.meta.env?.VITE_TIER_PRICE_IDS?.trim();
  if (!raw) return {};

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    console.warn("VITE_TIER_PRICE_IDS is not valid JSON; using the ids in plans.ts");
    return {};
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    console.warn("VITE_TIER_PRICE_IDS is not a JSON object; using the ids in plans.ts");
    return {};
  }

  /* Empty strings are dropped rather than substituted. An override that blanked a
     priceId would leave a plan that renders, offers a buy button and cannot be
     bought — the state `topUpAvailable` exists to prevent for the pack. */
  return Object.fromEntries(
    Object.entries(parsed as Record<string, unknown>)
      .filter(([, v]) => typeof v === "string" && v !== "")
      .map(([k, v]) => [k, v as string]),
  );
}

/** The seed with this build's ids swapped in. Substitutes only; never adds. */
function withTierPrices(seed: readonly PlanDef[]): readonly PlanDef[] {
  const overrides = tierPriceOverrides();
  return seed.map((plan) =>
    overrides[plan.id] ? { ...plan, priceId: overrides[plan.id] } : plan,
  );
}

/* The literal array stays directly under the PLANS export below, and that is load-
   bearing rather than stylistic: `TierPriceIdsMatchWhatTheBrowserSends` in RIPK reads
   this file as TEXT, splitting on that declaration and regexing the `priceId:`
   literals after it. Moving the seed into its own named const would leave that split
   looking at this wrapper, and the cross-repo drift check would find nothing to
   compare — it fails loudly rather than silently, which is how this comment came to
   be written the careful way round: the first draft QUOTED that declaration, the
   quotation became the first match, and the check went red on a comment. Do not name
   it exactly here. */
export const PLANS: readonly PlanDef[] = withTierPrices([
  {
    id: "premium",
    name: "Premium",
    /* Replaced 2026-08-26. The previous id charged $126.96 a month against the
       $129.96 this plan advertises — 12696 for 12996, one digit, and nothing
       compared the two numbers until `npm run verify:prices` did. A Stripe price
       is immutable once used, so the correction is a new price and this swap
       rather than an edit. The old id is archived. */
    /* Repriced to $9.99/month, 2026-09-03 (was $49.99 from 2026-08-31). Verified
       against the LIVE account before the swap, which is the check this file's
       own history says gets skipped: active, AUD, monthly, unit_amount 999,
       tax_behavior inclusive, on prod_V21YVEFDlAgmEW — the same "Premium"
       product the old id belonged to. A price is immutable once used, so this
       is a new id, not an edit; the old one is archived. */
    priceId: "price_1UBZTJ2Ni8JNdVHwasJrgpi6",
    monthlyPriceAud: 9.99,
    monthlyCredits: PREMIUM_CREDITS,
    blurb: "Every model we've built, one subscription.",
    /* "History kept for a year" was here and has been removed (2026-07-31).
       Conversation retention is a flat 24 hours on EVERY tier — the server TTL
       is the authority (api/kev_history.py) and it does not vary by tier. A perk
       line claiming twelve months against a one-day TTL is the same class of
       exposure as the "all models" bundle claim already on the security
       checklist: ACL misleading conduct, not a style nit.

       Retention is written as a per-tier function server-side, so when a longer
       Premium window is actually built this line comes back — sourced from that
       constant, not typed here from memory. */
    perks: [gamesPerk(9), "Longer answers", "Priority on new models"],
  },
  {
    id: "pro",
    name: "Pro",
    /* Swapped 2026-08-29 for the GST-INCLUSIVE price, matching RIPK a4c31f2. The
       amount is unchanged at $216.62/month; `tax_behavior` moved from exclusive
       to inclusive, so the charge is recorded as CONTAINING GST rather than
       sitting on top of it. A price is immutable once used, so a tax-treatment
       change is a new id, never an edit. Premium needed no swap — its price was
       already inclusive.

       TWO THINGS WOULD HAVE BROKEN, and both in prod. The server had already
       swapped, so this file would have sent `price_1U8vsR…` to a server expecting
       `price_1U9YZw…` and every Pro purchase would have been refused
       `price_grant_mismatch` — the warning at the top of this file, coming true a
       second time in the same direction. And the old price was `exclusive`, which
       adds GST ON TOP of the figure this site advertises as including it, and
       which the upgrade dialog now names as "Includes GST".

       Verified against the live account before the swap: active, tax_behavior
       inclusive, unit_amount 21662. */
    /* Repriced to $89.99/month, 2026-08-31. Verified against the LIVE account:
       active, AUD, monthly, unit_amount 8999, tax_behavior inclusive, on
       prod_V21ZWuYnlk2GnS ("Pro").

       ⚠️ THE ID FIRST SUPPLIED FOR PRO WAS THE FOOTBALL-SPORT PRICE — the same
       string as Kev football, $19.99 on prod_V9dHeZZ1hPC6jA. It was caught by
       reading every id back from Stripe before wiring any of them, not by
       looking at them. Two of this file's three price incidents were a correct-
       looking id for the wrong object; a `price_…` string carries no evidence of
       what it costs, so verify the amount and the product, never the shape.

       Pro is the plan this reprice is hardest on: at full credit use it clears
       single digits, and under the 30% founding discount it is sold below cost
       on purpose. PRO_CREDITS is what bounds that subsidy — see the warning at
       the top of this file before changing it. */
    /* Repriced to $19.99/month, 2026-09-03 (was $89.99 from 2026-08-31): active,
       AUD, monthly, unit_amount 1999, tax_behavior inclusive, on
       prod_V21ZWuYnlk2GnS "Pro" — read back by id before it was pasted, because
       $19.99 is ALSO what a per-model price used to cost and a wrong-product id
       at the right amount is exactly the mistake recorded above. */
    priceId: "price_1UBZSO2Ni8JNdVHwLIpMFFUr",
    monthlyPriceAud: 19.99,
    monthlyCredits: PRO_CREDITS,
    blurb: "For heavy use.",
    perks: [
      "Everything in Premium",
      gamesPerk(20),
      "Priority when things are busy",
    ],
  },
]);

/** Top-up pack — deliberately priced ABOVE every subscription rate, so buying
    volume via top-ups always costs more than upgrading. The ladder self-corrects
    and the daily cap stays a paywall, not a wall (guardrails §6.3).

    RAISED 5.99 → 7.99 on 2026-08-03 (Ned) because the reprice INVERTED that
    rule. The comparison that binds is MARGINAL, not average: what does the next
    tier charge for the extra credits it adds, against what the pack charges for
    the same credits? A subscription buys game caps and model access too, so its
    average per-credit rate is the wrong yardstick.

      per-model → Premium   ($129.96 − $64.96) ÷ (1500 − 450)  ≈ $0.0619/cr
      Premium   → Pro       ($216.62 − $129.96) ÷ (3300 − 1500) ≈ $0.0481/cr

    At $5.99/100cr the pack was $0.0599/cr — under the $0.0619 cost of stepping
    up from a single model, so that user was better off buying packs forever.
    $7.99/100cr is $0.0799/cr, above both steps, and upgrading wins again.

    This invariant is not self-enforcing: it is asserted in plans.test.ts. Any
    future price change has to re-check it, because it breaks silently — nothing
    fails, the ladder just quietly stops working.

    ⚠️ WAS NOT BUYABLE UNTIL 2026-08-10, and the three blockers listed here are
    now closed — kept, because the list is the map of what a top-up touches:

      - Nothing rendered it. There is a card on the pricing page and a button on
        the account page now, both gated on `priceId` below being set.
      - The server had no `topup` grant kind. handler_billing has one, plus the
        third branch in each of the four other places that were two-way
        (`price_matches_grant`, `_grant`, `_entitlement_landed`, `fulfilIfDue`).
      - kev_stripe.create_checkout_session hardcoded `mode: "subscription"`. It
        takes a `mode` now, and moves `subscription_data` → `payment_intent_data`
        with it, because Stripe REJECTS the former on a one-off.

    A fourth nobody had listed: the monthly reset does `SET balance`, so a pack
    granted into `balance` — the obvious way, and what model subs do — would have
    been deleted at the next cycle, quietly making "top-ups roll over" false on
    three surfaces that promise it. Bought credits live in their own attribute.

    ⚠️ `priceId` IS STILL NULL, AND THAT IS DELIBERATE, NOT UNFINISHED. The pack
    ships dark: every buy control is gated on this being set, and the server
    refuses with `no_price_for_grant` while `TOPUP_PRICE_ID` is unset. Creating
    the price in Stripe and pasting the id here — plus setting the env var — is
    the whole of switching it on. Null rather than a placeholder so it cannot be
    misread as an id someone forgot to paste. */
export const TOP_UP = { credits: 100, priceAud: 7.99, priceId: null } as const;

/** Can the pack actually be sold? One check, so the pricing page, the account
    page and the tests cannot disagree about whether it is on sale. */
export const topUpAvailable = (): boolean =>
  typeof TOP_UP.priceId === "string" && (TOP_UP.priceId as string).startsWith("price_");

/**
 * ═══ PURCHASING IS OFF AT LAUNCH, DELIBERATELY (Ned, 2026-08-26) ═══
 *
 * The site goes live with plans and models on display and every buy control
 * reading "Coming soon". Nothing about that is a placeholder or a stub: the
 * checkout flow works, and it is switched off.
 *
 * ═══ WHY A FLAG AND NOT A CHECK ON THE priceIds ═══
 *
 * `topUpAvailable` above can test its own id, because null is unambiguous. The
 * PLAN ids cannot be tested the same way — the warning at the top of this file
 * exists precisely because a test-mode id and a live one are both `price_…` and
 * NOTHING distinguishes them. A predicate that tried would return true today,
 * against prices that only exist in Stripe's test mode, and send a real customer
 * to a checkout that cannot charge them.
 *
 * So it is an explicit constant. Flipping it is a commit, made in the same
 * breath as pasting the live ids — which is the coupling that matters, and the
 * one a clever predicate would have hidden.
 *
 * ═══ WHAT TO DO WHEN GOING LIVE ═══
 *
 * Swap the ids above to live mode, set the default below to `true`, and do BOTH
 * in one change. Either alone is a broken state: live ids with purchasing off is
 * money left on the table, and purchasing on with test ids is a customer who
 * cannot pay and does not know why.
 *
 * ═══ WHY THERE IS AN ENV SEAM ═══
 *
 * `VITE_PURCHASING=off` turns it OFF for a build. It read `=on` and defaulted to
 * off until 2026-08-27, when the live price ids went in and this default
 * inverted. The seam was kept rather than replaced with a bare `true`, because a
 * kill switch that needs no code change is worth more once real cards are
 * involved than it was when nothing could be bought at all.
 *
 * It also still exists for its original reason: the
 * purchase round-trip has to keep being tested. `e2e/purchase.spec.ts` drives a
 * real browser through deep link → upsell → Subscribe → a genuine page leave →
 * webhook → entitlement → the chat that was paid for, and `mobile.spec.ts` does
 * it again on a phone. Gating the button turned all three red, and the honest
 * options were to delete coverage of the money path or to let the harness switch
 * it on. The same call `Pricing.subscriber.test.tsx` makes, for the same reason:
 * the double-billing guard has to survive the period when buying is switched
 * off.
 *
 * UNSET IS NOW TRUE, and that inversion IS the go-live switch. It was false for
 * as long as every price id in this file was test-mode, so no deploy could offer
 * to sell what it could not charge for. Both halves moved together, which is the
 * coupling that matters: live ids with purchasing off is money left on the table,
 * and purchasing on with test ids is a customer who cannot pay and is not told
 * why.
 *
 * ═══ THE PROD DEPLOY DOES NOT INHERIT THAT DEFAULT ═══
 *
 * This paragraph used to end "`deploy-web.sh` still sets nothing, so a deploy now
 * sells — and a build that must not sell has to pass `VITE_PURCHASING=off`
 * deliberately." That was true for about a day, and it is now the opposite of
 * what the script does. Corrected 2026-08-29.
 *
 * On the SAME DATE this default inverted (2026-08-27), `deploy-web.sh` grew a
 * second interlock pointing the other way: a PROD build exports
 * `VITE_PURCHASING=off` unless the operator passes `KEV_PROD_PURCHASING=on`. Dev
 * still unsets it and always sells, which is where the money path is proven.
 *
 *     To ship prod selling:  KEV_PROD_PURCHASING=on ./deploy-web.sh prod
 *
 * THE TWO DEFAULTS POINT OPPOSITE WAYS ON PURPOSE, and that is not a
 * contradiction waiting to be tidied away. They answer different questions:
 *
 *   * this module's default answers "MAY this build sell?" — and unset-is-true
 *     is what keeps every unit test, every dev build and every CI run exercising
 *     the real purchase path rather than a disabled button;
 *   * the script's default answers "did a human mean for THIS PROD PUSH to
 *     sell?" — and off-by-default is what stops a forgotten environment variable
 *     putting a live Subscribe button in front of the public.
 *
 * Making either agree with the other deletes one of those two protections. The
 * failure they guard is asymmetric and that is why the polarity is: forgetting
 * the flag ships a site that cannot sell, which is visible the moment anyone
 * tries to buy; the reverse charges real cards nobody authorised.
 *
 * ⚠️ THE COST OF THAT SAFETY IS THAT FORGETTING IS SILENT TO EVERYONE ELSE.
 * Existing subscribers keep access (Pricing.tsx checks ownership BEFORE this
 * flag), so a prod deploy that omits the variable looks entirely healthy and
 * simply stops taking new money. If you need to know which way a deployed bundle
 * went, the flag is constant-folded out of the build — grep cannot see it, and
 * the Subscribe branch survives either way. Rebuild with the deploy's exact env
 * and compare the emitted asset hash against what is live; the two settings
 * produce different hashes.
 */
/* `import.meta.env?.` — the optional chain is load-bearing outside the browser.
   Vite defines `import.meta.env` at build time, but Node running this file
   directly does not, and `scripts/` imports from `src/` on purpose so a check
   computes prices with the SAME code the pricing page renders. Without the `?.`
   every such script dies with "Cannot read properties of undefined", which is
   how `verify-prices.ts` found this. */
export const PURCHASING_LIVE = import.meta.env?.VITE_PURCHASING !== "off";

/** One check, so the pricing page and the tests cannot disagree about whether
    anything is actually for sale. Mirrors `topUpAvailable` on purpose. */
export const purchasingAvailable = (): boolean => PURCHASING_LIVE;

/** Monthly credits granted by ONE per-model subscription. Product config, so it
    lives here — the mock backend imports it rather than owning a copy. */
export const PER_MODEL_CREDITS = 450;

export function planFor(tier: TierId): PlanDef | undefined {
  return PLANS.find((p) => p.id === tier);
}

/**
 * What each tier is CALLED, as opposed to what it is called on the wire.
 *
 * `free` stays `free` in `tierIdSchema` and in every request and response — the
 * server, the Stripe metadata and the entitlement records all key on it, and
 * renaming an identifier to fix a label would be a contract change made for a
 * copy reason. This map is the whole rename: one place, display only.
 *
 * "Free" became "Basic" on 2026-08-29 (Ned) because it was ambiguous in exactly
 * the case the product is built around. Tier and per-model subscriptions are
 * independent — someone on the `free` tier who has bought one model has a real
 * subscription, 450 credits a month and a model they pay for, and calling that
 * account "Free" told them the opposite. Account.tsx's credits card already
 * documents the same collision from the other side ("a free account holding one
 * model sub genuinely does have 450 a month"). "Basic" names the floor without
 * making a claim about what has been bought on top of it.
 *
 * ⚠️ This does NOT rename the SIGN-UP offer. "An account costs nothing" and
 * "Create a free account" (content/landing.ts) are price claims aimed at someone
 * who has no account at all, where there is nothing to be ambiguous with, and
 * they are the offer itself. Free-as-a-price is fine; Free-as-a-tier-name is
 * what was wrong.
 */
/* The paid two are READ FROM `PLANS`, never retyped. This file's whole posture
   is that a second copy of a number is how two surfaces come to disagree, and a
   plan's NAME is no different: typing "Premium" here would let the account page
   and the pricing page drift apart on what the same subscription is called.
   Only `free` has a literal, because it is the one tier with no PlanDef — it is
   what you are on when you have not bought a plan. */
const planName = (id: PlanDef["id"]): string => {
  const plan = PLANS.find((p) => p.id === id);
  /* Unreachable while PLANS covers both paid ids, which plans.test.ts asserts.
     Falling back to the id keeps a label bug from becoming a blank page. */
  return plan?.name ?? id;
};

export const TIER_LABEL: Record<TierId, string> = {
  free: "Basic",
  premium: planName("premium"),
  pro: planName("pro"),
};
